import { createElement, memo, useMemo } from 'react';

import { getFileIcon, getFileType } from '../../utils';
import { PdfThumbnailWrapper } from './PdfThumbnailWrapper';

export interface FilePreviewProps {
  fileName: string;
  fileUrl?: string;
  width: number;
  height: number;
  previewClassName?: string;
  iconClassName?: string;
  pdfThumbnailClassName?: string;
  pdfLoadingClassName?: string;
}

/**
 * Shared component for rendering file previews (images, PDFs, videos) or file icons
 * Handles all preview types and fallbacks consistently
 */
export const FilePreview: React.FC<FilePreviewProps> = memo(
  ({
    fileName,
    fileUrl,
    width,
    height,
    previewClassName,
    iconClassName,
    pdfThumbnailClassName,
    pdfLoadingClassName,
  }) => {
    const FileIconComponent = useMemo(() => getFileIcon({ fileName }), [fileName]);

    const fileType = useMemo(() => getFileType(fileName), [fileName]);

    const shouldShowPreview = useMemo(() => {
      return fileUrl && (fileType === 'image' || fileType === 'pdf' || fileType === 'video');
    }, [fileUrl, fileType]);

    const fallbackIcon = createElement(FileIconComponent, { className: iconClassName });

    if (!fileUrl || !shouldShowPreview) {
      return fallbackIcon;
    }

    if (fileType === 'image') {
      return <img src={fileUrl} alt={fileName} className={previewClassName} loading="lazy" />;
    }

    if (fileType === 'pdf') {
      return (
        <PdfThumbnailWrapper
          fileUrl={fileUrl}
          width={width}
          height={height}
          className={pdfThumbnailClassName}
          loadingClassName={pdfLoadingClassName}
          fallbackIcon={fallbackIcon}
        />
      );
    }

    if (fileType === 'video') {
      return (
        <video src={fileUrl} className={previewClassName} preload="metadata" muted playsInline>
          <source src={fileUrl} />
        </video>
      );
    }

    return fallbackIcon;
  },
);

FilePreview.displayName = 'FilePreview';
